/**
 * RBAC-ENF1 · C · 前端按「权限点」显示 / 隐藏（不再按角色名判断）。
 *
 * 数据来源：`GET /api/v1/auth/me` 返回 `permissions: string[]`（该用户所有角色权限点并集），登录后存进 authStore。
 * 前端只负责「不该看的不显示」；真正的拦截在后端（rbac/guard.py），绕过前端也没用。
 *
 * 约定：
 * - `permissions` 为 undefined（旧会话 / mock 模式 / /me 还没回来）= 先不隐藏，避免闪空白；/me 回来后立即收紧。
 * - 无权限的新建 / 编辑 / 删除 / 审批按钮**直接不渲染**（不是置灰），用 <Can> 包。
 *
 *   <Can perm="vehicles.vehicles.create"><Button>新建车辆</Button></Can>
 *   <Can anyOf={["finance.invoices.list", "finance.reports.view"]}>…</Can>
 *   const canEdit = useCan("hotels.hotels.update");
 */
import type { ReactNode } from "react";
import { useAuthStore } from "@/store/authStore";

/** 纯函数版（非组件里用）：permissions 为 undefined 视为放行。 */
export function hasPermission(perms: readonly string[] | undefined, code: string): boolean {
  if (perms === undefined) return true;
  return perms.includes(code);
}

export function hasAnyPermission(perms: readonly string[] | undefined, codes: readonly string[]): boolean {
  if (perms === undefined) return true;
  return codes.some((c) => perms.includes(c));
}

export function useCan(code: string): boolean {
  const perms = useAuthStore((s) => s.user?.permissions);
  return hasPermission(perms, code);
}

export function useCanAny(codes: readonly string[]): boolean {
  const perms = useAuthStore((s) => s.user?.permissions);
  return hasAnyPermission(perms, codes);
}

interface CanProps {
  /** 需要这一个权限点 */
  perm?: string;
  /** 需要其中任意一个 */
  anyOf?: readonly string[];
  /** 没权限时显示什么（默认什么都不显示 = 隐藏，不置灰） */
  fallback?: ReactNode;
  children: ReactNode;
}

export function Can({ perm, anyOf, fallback = null, children }: CanProps) {
  const perms = useAuthStore((s) => s.user?.permissions);
  const ok = anyOf ? hasAnyPermission(perms, anyOf) : perm ? hasPermission(perms, perm) : true;
  return <>{ok ? children : fallback}</>;
}
